import * as React from 'react';
import {useEffect, useState} from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid2 as Grid,
    MenuItem,
    Select,
    TextField,
    Tooltip
} from '@mui/material';
import {Add, ArrowDropUp, Delete, Edit, FilterAltOff, KeyboardArrowDown, Save} from '@mui/icons-material';
import {useNavigate} from 'react-router-dom';
import {useTitle} from '../dashboard/TitleContext';
import {useSnackbar} from "../../util/SnackbarProvider";
import TablePagination from "@mui/material/TablePagination";
import FilterPanel from "../common/FilterPanel";
import IconButton from "@mui/material/IconButton";
import {ArrowDropDownIcon} from "@mui/x-date-pickers";
import ConfirmationDialog from "../common/ConfirmationDialog";
import taskTypeService from "../../services/TaskTypeService";

const paymentCategoryMap = {
    "PROGRAMMING_AND_PLANNING": "Programozás és Tervezés",
    "ELECTRICAL_INSTALLATION": "Villanyszerelés",
    "ACCOMMODATION": "Szállás",
    "CATEGORY_TYPE_4": "Kategória 4",
    "CATEGORY_TYPE_5": "Kategória 5",
    "TRAVELLING": "Utazás",
    "OTHER": "Egyéb"
};

const ALL_COLUMNS = [
    {id: 'name', label: 'Feladatkör neve', editable: true, searchable: true},
    {id: 'hourlyWage', label: 'Óradíj', type: "number", editable: true},
    {id: 'hourlyRate', label: 'Rezsi óradíj', type: "number", editable: true},
    {id: 'paymentModifier', label: 'Fizetési Módosító', type: "number", editable: true},
    {id: 'paymentCategory', label: 'Fizetési Kategória', fun: v => paymentCategoryMap[v], editable: true}
];


const DEFAULT_COLUMNS = ['name', 'hourlyWage', 'hourlyRate', 'paymentModifier', 'paymentCategory'];
const LOCAL_STORAGE_ITEM_NAME = "task-types-rows-per-page"
const LOCAL_STORAGE_SEARCH_PARAMS = "task-types-search-params"
const LOCAL_STORAGE_COLUMN_NAMES = "task-types-column-names"
const DEFAULT_SEARCH_PARAMS = {}

export default function TaskTypeTable() {

    useTitle([{name: 'Feladatkörök'}])

    const {sendAlertMessage} = useSnackbar()
    const navigate = useNavigate();
    const [dialogState, setDialogState] = React.useState({open: false, object: null})
    const [tableData, setTableData] = useState({
        page: 0,
        rowsPerPage: localStorage.getItem(LOCAL_STORAGE_ITEM_NAME) || 25,
        totalCount: 0
    })
    const [searchParams, setSearchParams] = useState(JSON.parse(localStorage.getItem(LOCAL_STORAGE_SEARCH_PARAMS)) || DEFAULT_SEARCH_PARAMS)
    const [columnNames, setColumnNames] = React.useState(JSON.parse(localStorage.getItem(LOCAL_STORAGE_COLUMN_NAMES)) || DEFAULT_COLUMNS)
    const [expanded, setExpanded] = useState(true);
    const handleDeletionClick = (object) => {
        if (object.id == null) retrieveTaskTypes()
        else setDialogState({open: true, object: object})
    }
    const handleClose = () => setDialogState({open: false, object: null})
    const [indexOfEditedRow, setIndexOfEditedRow] = React.useState();
    const filterPanelRef = React.useRef();

    useEffect(() => {
        retrieveTaskTypes()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableData.page, tableData.rowsPerPage, searchParams])

    const retrieveTaskTypes = () => {
        taskTypeService.search(buildQueryParams())
            .then(response => setTableData((prev) => ({
                ...prev,
                totalCount: response.totalElements,
                rows: response.content
            })))
            .catch(error => sendAlertMessage(error?.message || "Feladatkörök lekérése sikertelen", 'error'))
    }

    function buildQueryParams() {
        return {
            page: tableData.page,
            size: tableData.rowsPerPage,
            ...(searchParams?.name && {name: searchParams.name})
        }
    }

    const deleteTaskType = (id) => {
        taskTypeService.deleteTaskType(id)
            .then(() => {
                sendAlertMessage('Feladatkör sikeresen törölve')
                retrieveTaskTypes()
            })
            .catch(error => sendAlertMessage(error?.message || 'Feladatkör törlése sikertelen', 'error'))
    }

    const onSearchParamsChange = (event, n, v) => {
        let name = event?.target?.name || n
        let value = event?.target?.value || v
        setSearchParams((prev) => ({
            ...prev,
            [name]: value
        }))
    }

    const clear = () => {
        if (searchParams != null) sendAlertMessage('Szűrők alaphelyzetbe állítva', 'info')
        setSearchParams(DEFAULT_SEARCH_PARAMS)
        filterPanelRef.current?.resetSelection();
    }

    useEffect(() => {
        if (searchParams) localStorage.setItem(LOCAL_STORAGE_SEARCH_PARAMS, JSON.stringify(searchParams));
        else localStorage.removeItem(LOCAL_STORAGE_SEARCH_PARAMS)
    }, [searchParams]);

    const editOrSaveClick = (e, index) => {
        if (indexOfEditedRow != null) {
            persistTaskType(e, index)
            setIndexOfEditedRow(null)
        } else setIndexOfEditedRow(index)
    }

    const iconButtonStyle = {
        position: "relative",
        top: "0px",
        left: "0px",
        width: "24px",
        color: "#808080",
        cursor: "pointer"
    }

    const onChangeColumnCheckBox = (event) => {
        const {checked, name} = event.target;
        setColumnNames((prev) => {
            return checked ? prev.includes(name) ? prev : [...prev, name] : prev.filter((col) => col !== name);
        });
    };

    useEffect(() => {
        if (columnNames) localStorage.setItem(LOCAL_STORAGE_COLUMN_NAMES, JSON.stringify(columnNames));
        else localStorage.removeItem(LOCAL_STORAGE_COLUMN_NAMES)
    }, [columnNames]);

    const addEmptyRow = () => {
        setTableData(prev => ({
            ...prev,
            rows: [{paymentCategory: "OTHER"}, ...prev.rows
            ]
        }));
        setIndexOfEditedRow(0)
    };


    function onChangeTaskType(e, index) {
        const {name, value} = e.target;

        setTableData(prev => {
            const newRows = [...prev.rows];
            newRows[index] = {...newRows[index], [name]: value};

            return {
                ...prev,
                rows: newRows
            };
        });
    }


    const persistTaskType = (e, index) => {
        e.preventDefault();
        const taskType = tableData.rows[index]
        if (taskType.id)
            taskTypeService.updateTaskType(taskType.id, taskType)
                .then(() => {
                    sendAlertMessage('Feladatkör sikeresen módosítva')
                    retrieveTaskTypes()
                })
                .catch(error => sendAlertMessage(error?.message || 'Feladatkör módosítása sikertelen', 'error'))
        else taskTypeService.createTaskType(taskType)
            .then(() => {
                sendAlertMessage('Feladatkör sikeresen hozzáadva')
            })
            .catch(error => sendAlertMessage(error?.message || 'Feladatkör mentése sikertelen', 'error'))
            .finally(() => retrieveTaskTypes())
    }

    return (
        <>
            <Accordion style={{marginBottom: "10px"}} disableGutters expanded={expanded}
                       onChange={() => setExpanded(!expanded)}>
                <AccordionSummary
                    expandIcon={<KeyboardArrowDown sx={{marginRight: "0px", height: "2em", width: "auto"}}/>} sx={{
                    minHeight: "0px",
                    fontSize: "24px",
                    fontWeight: "bold",
                    backgroundColor: "darkgray",
                    borderRadius: "4px 4px 0px 0px",
                    paddingLeft: "20px",
                    margin: "0"
                }}>
                    Keresési Feltételek
                </AccordionSummary>
                <AccordionDetails className="filter-panel">
                    <Grid container columns={10} spacing={2} padding={"10px"}>
                        <Grid size={{xs: 7}}>
                            <Grid container columns={20} spacing={2} padding={"10px"}>
                                <Grid size={{xs: 20}}>
                                    {ALL_COLUMNS.map((item, index) => (
                                        <FormControlLabel key={index}
                                                          control={<Checkbox
                                                              variant="outlined"
                                                              margin="normal"
                                                              size="large"
                                                              name={item.id}
                                                              onChange={onChangeColumnCheckBox}
                                                              checked={columnNames?.includes(item.id) || false}
                                                              sx={{padding: "0px"}}
                                                          />} label={item.label?.props?.title || item.label}
                                                          style={{paddingRight: "20px", margin: "0px"}}/>
                                    ))}
                                </Grid>

                            </Grid>
                        </Grid>
                        <Grid size={{xs: 3}}>
                            <FilterPanel
                                type="TASK_TYPE"
                                ref={filterPanelRef}
                                currentSearchParams={searchParams}
                                onFilterSelect={(params) => setSearchParams(params)}
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <Paper sx={{width: '100%', overflow: 'hidden', fontSize: '10px'}}>
                <TableContainer>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                <TableCell key="actions"
                                           style={{width: "100px", paddingLeft: "10px"}}
                                           align='left' padding='none'>
                                    <Tooltip title="Új hozzáadása">
                                        <Button variant='contained' color='success'
                                                sx={{minWidth: "0px", padding: "0px", marginRight: "10px"}}
                                                onClick={() => addEmptyRow()}>
                                            <Add fontSize="large"/>
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Szűrők törlése">
                                        <Button variant='contained' color='info'
                                                sx={{minWidth: "0px", padding: "0px", marginRight: "10px"}}
                                                onClick={clear}>
                                            <FilterAltOff fontSize="large"/>
                                        </Button>
                                    </Tooltip>
                                </TableCell>
                                {ALL_COLUMNS.filter(column => columnNames.includes(column.id)).map((column, index) => (
                                    <TableCell key={index} sx={{padding: "8px"}}>
                                        {column.searchable ? <TextField name={column.id} label={column.label} size="small"
                                                   value={searchParams[column.id] || ""} onChange={onSearchParamsChange}
                                                   sx={{margin: "0px", width: "calc(100% - 40px)"}} autoComplete="off"/> : column.label}
                                        <IconButton onClick={() => setSearchParams(prev => ({
                                            ...prev,
                                            orderBy: column.id,
                                            sort: searchParams.sort === 'ASC' ? 'DESC' : 'ASC'
                                        }))}>
                                            {searchParams.sort == null || searchParams.orderBy !== column.id ?
                                                <ArrowDropDownIcon sx={{color: "lightgray"}}/> :
                                                searchParams.sort === 'ASC' ? <ArrowDropDownIcon/> : <ArrowDropUp/>}
                                        </IconButton>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableData.rows == null ?
                                <TableRow sx={{textAlign: "center"}}>
                                    <TableCell colSpan={columnNames?.length || 8}
                                               sx={{padding: "200px !important", textAlign: "center"}}>
                                        <CircularProgress/>
                                    </TableCell>
                                </TableRow> :
                                <>
                                    {tableData.rows?.map((row, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell padding='none' align='center' sx={{lineHeight: "0px"}}>
                                                <div style={{
                                                    display: "inline-flex",
                                                    padding: "2px",
                                                    width: "100%",
                                                    height: "30px !important",
                                                }}>
                                                    <Tooltip
                                                        title={indexOfEditedRow === index ? "Mentés" : "Szerkesztés"}
                                                        placement="left">
                                                        {indexOfEditedRow === index ?
                                                            <Save onClick={e => editOrSaveClick(e, index)}
                                                                  sx={iconButtonStyle}/> :
                                                            <Edit onClick={e => editOrSaveClick(e, index)}
                                                                  sx={iconButtonStyle}/>
                                                        }
                                                    </Tooltip>
                                                    <Tooltip title={"Törlés"} placement="left">
                                                        <Delete onClick={() => handleDeletionClick(row)} sx={{
                                                            position: "relative",
                                                            top: "0px",
                                                            left: "0px",
                                                            width: "24px",
                                                            color: "#808080",
                                                            cursor: "pointer"
                                                        }}/>
                                                    </Tooltip>
                                                </div>
                                            </TableCell>

                                            {ALL_COLUMNS.filter(c => columnNames.includes(c.id)).map((column, columnIndex) => {
                                                let value = row[column.id]
                                                let valueToShow = column.fun ? column.fun(value) : value
                                                return (<TableCell key={columnIndex} align={column.align} sx={{
                                                    padding: "0px 10px",
                                                    '& input': {fontSize: "0.8rem !important", padding: "0px"}
                                                }}>
                                                    {(indexOfEditedRow === index && column.editable) ?
                                                        (column.id ===  'paymentCategory' ?
                                                            <FormControl fullWidth>
                                                                <Select
                                                                    variant="standard"
                                                                    required
                                                                    fullWidth
                                                                    id="paymentCategory"
                                                                    name="paymentCategory"
                                                                    onChange={e => onChangeTaskType(e, index)}
                                                                    value={value || ''}
                                                                    sx={{textAlign: "left", fontSize: "0.8rem !important", "& div": {padding: "0px"}}}>
                                                                    >
                                                                    {Object.entries(paymentCategoryMap).map(([key, value]) => (
                                                                        <MenuItem value={key}>{value}</MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                            :
                                                            <TextField name={column.id}
                                                                       variant='standard' required={column.required}
                                                                       onChange={e => onChangeTaskType(e, index)}
                                                                       value={value || ''}
                                                                       type={column.type || "text"}>
                                                            </TextField>)
                                                        : (valueToShow == null ? "" : valueToShow)}
                                                </TableCell>)
                                            })}

                                        </TableRow>
                                    ))}
                                </>}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[3, 10, 25, 50]}
                    component="div"
                    count={tableData.totalCount}
                    rowsPerPage={parseInt(tableData.rowsPerPage)}
                    page={tableData.page}
                    onPageChange={(event, newPage) => setTableData((prev) => ({...prev, page: newPage}))}
                    onRowsPerPageChange={(event) => {
                        setTableData((prev) => ({...prev, rowsPerPage: event.target.value, page: 0}))
                        localStorage.setItem(LOCAL_STORAGE_ITEM_NAME, event.target.value)
                    }}
                    labelRowsPerPage="Elemek oldalanként:"
                />
            </Paper>

            <ConfirmationDialog
                title={"Biztosan törölni szeretnéd ezt a feladatkört?"}
                content={
                    <>
                        {dialogState.object?.name} <br/>
                    </>
                }
                open={dialogState.open}
                onClose={handleClose}
                onConfirm={() => {
                    deleteTaskType(dialogState.object?.id)
                }}
            />
        </>
    )
}
